import {Question} from './Question'
import {AbstractControl, UntypedFormControl, UntypedFormGroup} from '@angular/forms'
import {requireSelectionOnEachMatrixRow} from 'app/form-validators/RequireSelectionOnEachMatrixRowValidator'
import {requireCommentOnOpenSelection} from 'app/form-validators/RequireCommentOnOpenSelectionValidator'
import {IMatrix} from 'app/interfaces/IMatrix'
import {MatrixPropertyValidator} from 'app/models/questions/property-validator/MatrixPropertyValidator'
import {IRow} from 'app/interfaces/IRow'
import {requireMinRowSelection} from 'app/form-validators/RequireMinRowSelectionValidator'
import {FormFillingError} from 'app/custom-errors/FormFillingError'
import {requireMinStatementSelection} from 'app/form-validators/RequireMinStatementSelectionValidator'
import {imageUploadValidator} from "../../form-validators/ImageUploadValidator";
import {audioRecorderValidator} from "../../form-validators/AudioRecorderValidator";

export abstract class Matrix extends Question implements IMatrix {

  private readonly _randomizeRows: boolean
  private readonly _rowsRulesIndex: string[]
  private readonly _useImage: boolean

  private _rowsOrder: string[]
  private _rows: Record<string, IRow>

  constructor(question: any) {
    super(question)

    this._useImage = question.useImage
    this._rowsOrder = question.rowsOrder
    this._rows = question.rows
    this._randomizeRows = question.randomizeRows
    this._rowsRulesIndex = question.rowsRulesIndex

    new MatrixPropertyValidator(question).validate()
  }

  formatFormPayload(surveyForm: UntypedFormGroup): Record<string, {}> {
    // Utiliser getRawValue() pour inclure toutes les valeurs, y compris false
    // form.value peut exclure certaines valeurs false dans certains cas
    // getRawValue() garantit que toutes les valeurs sont incluses, même false et même pour les contrôles désactivés
    const questionFormControl = surveyForm.get(this.key);
    if (questionFormControl && typeof questionFormControl.getRawValue === 'function') {
      return questionFormControl.getRawValue();
    }
    return questionFormControl?.value || surveyForm.get(this.key)?.value;
  }

  fillFormWithDummyAnswer(surveyForm: UntypedFormGroup, iterator = 0, maxRetries = 200) {
    if (surveyForm.controls[this.key].valid && surveyForm.controls[this.key].dirty) {
      return
    }

    if (iterator >= maxRetries) {
      throw new FormFillingError().cannotMakeQuestionValid(this.key, iterator)
    }

    this.resetForm(surveyForm)

    this.fillRandomControl(surveyForm)

    surveyForm.controls[this.key].markAsDirty()

    iterator++
    this.fillFormWithDummyAnswer(surveyForm, iterator, maxRetries)
  }

  fillRandomControl(surveyForm: UntypedFormGroup) {
    let statementKeys = this.extractStatementKeysFromSurveyForm(surveyForm)
    let rowKeys = this.extractRowKeysFromSurveyForm(surveyForm)

    rowKeys.forEach(rKey => {
      let availableControls = {}

      this.addAvailableControlsForRow(statementKeys, surveyForm, rKey, availableControls)

      const randomStatementKey = this.extractRandomObjectProperty(availableControls)
      const randomSelectedStatementControl = surveyForm.controls[this.key].get(rKey).get(this.formControlName).get(randomStatementKey)
      const randomCommentStatementControl = surveyForm.controls[this.key].get(rKey).get(this.commentsControlName).get(randomStatementKey)

      if (!this.isOpenStatement(rKey, randomStatementKey) && randomSelectedStatementControl) {
        randomSelectedStatementControl.setValue(true, {emitEvent: false})
        this.applyMaxAnswers(surveyForm)
        this.resetOtherStatementsOnUniqueMentionStatementSelection(rKey, randomStatementKey, surveyForm.get(this.key))
        return
      }

      if (randomSelectedStatementControl) {
        randomSelectedStatementControl.setValue(true, {emitEvent: false})
      }

      if (randomCommentStatementControl) {
        randomCommentStatementControl.setValue(this.generateLoremIpsum(), {emitEvent: false})
      }

      this.applyMaxAnswers(surveyForm)
      this.resetOtherStatementsOnUniqueMentionStatementSelection(rKey, randomStatementKey, surveyForm.get(this.key))
    })
  }

  resetOtherStatementsOnUniqueMentionStatementSelection(rKey: string, statementKey: string, formGroup: AbstractControl) {
  }

  addAvailableControlsForRow(statementKeys: string[], surveyForm: UntypedFormGroup, rKey: string, availableControls: {}) {
    statementKeys.forEach(sKey => {
      surveyForm.controls[this.key].get(rKey).get(this.formControlName).get(sKey).updateValueAndValidity()
      if (surveyForm.controls[this.key].get(rKey).get(this.formControlName).get(sKey).enabled) {
        if (!availableControls[sKey]) {
          availableControls[sKey] = true
        }
      }
    })
  }

  resetForm(surveyForm: UntypedFormGroup) {
    surveyForm.enable({emitEvent: false})

    let emptyMatrixAnswers = {}

    this.extractRowKeysFromSurveyForm(surveyForm).forEach(rKey => {
      if (!emptyMatrixAnswers[rKey]) {
        emptyMatrixAnswers[rKey] = {}
        emptyMatrixAnswers[rKey][this.formControlName] = {}
        emptyMatrixAnswers[rKey][this.commentsControlName] = {}
      }
      this.extractStatementKeysFromSurveyForm(surveyForm).forEach(sKey => {
        emptyMatrixAnswers[rKey][this.formControlName][sKey] = false
        emptyMatrixAnswers[rKey][this.commentsControlName][sKey] = ''
      })
    })

    surveyForm.controls[this.key].reset(emptyMatrixAnswers, {emitEvent: false})
  }

  buildQuestionFormGroup(): UntypedFormGroup {
    const formGroup = new UntypedFormGroup({}, this.buildFormGroupValidators())
    this.rowsOrder.forEach(rKey => {
      const validators = this.isOptional ? [] :
        [requireCommentOnOpenSelection(this.formControlName, this.commentsControlName, this.getOpenStatementsForRow(rKey))]
      let statementFormGroup = new UntypedFormGroup(
        {
          [this.formControlName]: this.buildStatementFormGroup(),
          [this.commentsControlName]: this.buildCommentsStatementFormGroup(rKey),
          [this.imageControlName]: this.buildImageStatementFormGroup(this.isOptional),
          [this.audioControlName]: this.buildAudioStatementFormGroup(this.isOptional)
        }, {
          validators,
          // updateOn: 'blur', // <---- Évite la sauvegarde vers firebase de manière intensive...
        })
      formGroup.addControl(rKey, statementFormGroup)
    })
    return formGroup
  }

  isOpenStatement(rowKey, statementKey): boolean {
    if (!this.rows[rowKey]) {
      return false
    }

    return this.rows[rowKey].openCols[statementKey]
  }

  getOpenStatementsForRow(rowKey: string): string[] {
    let openStatements = []
    const row = this.rows[rowKey]
    Object.keys(row.openCols).forEach(cKey => {
      if (row.openCols[cKey]) {
        openStatements.push(cKey)
      }
    })

    return openStatements
  }

  resetPreviousRadioStatementFormSelection(rKey: string, statementKey: string, formGroup: AbstractControl) {
    this.statementKeys.forEach(sKey => {
      if (sKey !== statementKey) {
        formGroup.get(rKey).get(this.formControlName).get(sKey).setValue(false, {onlySelf: true})
      }
    })
  }

  applyMaxAnswers(surveyForm: UntypedFormGroup) {
    if (!this.limitAnswersAmount) {
      return false
    }

    let matrixForm = surveyForm.controls[this.key]

    const rowKeys = this.extractRowKeysFromSurveyForm(surveyForm)
    const statementKeys = this.extractStatementKeysFromSurveyForm(surveyForm)

    if (this.maxAnswers.rows) {

      rowKeys.forEach(rKey => {
        let selectedStatementCount = 0
        let selectedStatements = matrixForm.get(rKey).get(this.formControlName).value

        selectedStatementCount = this.countSelectedStatementsForRow(selectedStatements, selectedStatementCount, surveyForm)

        this.disableStatementsByRowIfSelectionHitsThreshold(selectedStatementCount, matrixForm, rKey, surveyForm)
        this.reEnableRowStatementsIfSelectionFallsBelowThreshold(selectedStatementCount, matrixForm, rKey)
      })
    }

    if (this.maxAnswers.statements) {
      statementKeys.forEach(sKey => {
        let selectedStatementCountForColumn = 0
        rowKeys.forEach(rKey => {
          selectedStatementCountForColumn = this.countSelectedStatementsForColumn(matrixForm, rKey, sKey, selectedStatementCountForColumn)
          this.disabledStatementsByColumnIfSelectionHitsThreshold(selectedStatementCountForColumn, matrixForm, sKey, surveyForm)
        })
      })
    }
    return true
  }

  getSelectedStatementsCount(formControl: AbstractControl) {
    let selectedStatementsCount = 0
    this.rowKeys.forEach(rKey => {
      this.statementKeys.forEach(sKey => {
        if (formControl.get(rKey).get(this.formControlName).get(sKey).value) {
          selectedStatementsCount++
        }
      })
    })
    return selectedStatementsCount
  }

  extractStatementKeysFromSurveyForm(surveyForm: UntypedFormGroup) {
    const singleRowControl = surveyForm.getRawValue()[this.key][this.rowsOrder[this.rowsOrder.length - 1]]
    return singleRowControl ? Object.keys(singleRowControl[this.formControlName]) : []
  }

  extractRowKeysFromSurveyForm(surveyForm: UntypedFormGroup) {
    return Object.keys(surveyForm.getRawValue()[this.key])
  }

  countSelectedStatementsForColumn(matrixForm: AbstractControl, rKey: string, sKey: string, selectedStatementCountForColumn: number) {
    if (matrixForm.get(rKey).get(this.formControlName).get(sKey).enabled) {
      if (matrixForm.get(rKey).get(this.formControlName).get(sKey).value) {
        selectedStatementCountForColumn++
      }
    }
    return selectedStatementCountForColumn
  }

  disabledStatementsByColumnIfSelectionHitsThreshold(selectedStatementCountForColumn: number, matrixForm: AbstractControl, sKey: string, surveyForm: UntypedFormGroup) {
    if (selectedStatementCountForColumn === this.maxAnswers.statements) {
      this.extractRowKeysFromSurveyForm(surveyForm).forEach(rKey => {
        if (!matrixForm.get(rKey).get(this.formControlName).get(sKey).value) {
          matrixForm.get(rKey).get(this.formControlName).get(sKey).disable({emitEvent: false})
        }
      })
    }
  }

  countSelectedStatementsForRow(selectedStatements, selectedStatementCount: number, surveyForm: UntypedFormGroup) {
    this.extractStatementKeysFromSurveyForm(surveyForm).forEach(sKey => {
      if (selectedStatements[sKey]) {
        selectedStatementCount++
      }
    })
    return selectedStatementCount
  }

  disableStatementsByRowIfSelectionHitsThreshold(selectedStatementCount: number, matrixForm: AbstractControl, rKey: string, surveyForm: UntypedFormGroup) {
    if (selectedStatementCount === this.maxAnswers.rows) {
      this.extractStatementKeysFromSurveyForm(surveyForm).forEach(sKey => {
        if (!matrixForm.get(rKey).get(this.formControlName).get(sKey).value) {
          matrixForm.get(rKey).get(this.formControlName).get(sKey).disable({emitEvent: false})
        }
      })
    }
  }

  reEnableRowStatementsIfSelectionFallsBelowThreshold(selectedStatementCount: number, matrixForm: AbstractControl, rKey: string) {
    if (selectedStatementCount < this.maxAnswers.rows) {
      this.statementKeys.forEach(sKey => {
        if (matrixForm.get(rKey).enabled) {
          matrixForm.get(rKey).get(this.formControlName).get(sKey).enable({emitEvent: false})
        }
      })
    }
  }

  buildFormGroupValidators() {
    let validators = []

    if (!this.isOptional) {

      validators.push(requireMinRowSelection(this))
      validators.push(requireMinStatementSelection(this))
      validators.push(requireSelectionOnEachMatrixRow(this))

    }
    return validators
  }

  buildStatementFormGroup(): UntypedFormGroup {
    const form = new UntypedFormGroup({})
    this.statementKeys.forEach(sKey => {
      form.addControl(sKey, new UntypedFormControl(false))
    })
    return form
  }

  private buildCommentsStatementFormGroup(rKey: string): UntypedFormGroup {
    const form = new UntypedFormGroup({})
    this.getOpenStatementsForRow(rKey).forEach(cKey => {
      form.addControl(cKey, new UntypedFormControl(''))
    })

    return form
  }

  private buildImageStatementFormGroup(optional: boolean): UntypedFormGroup {
    const form = new UntypedFormGroup({});
    ((this.viewerType === 'imageUpload') ? this.statementKeys : []).forEach(cKey => {
      form.addControl(cKey, new UntypedFormControl('', optional ? [] : imageUploadValidator()))
    })

    return form
  }

  private buildAudioStatementFormGroup(optional: boolean): UntypedFormGroup {
    const form = new UntypedFormGroup({});
    ((this.viewerType === 'audioRecorder') ? this.statementKeys : []).forEach(cKey => {
      form.addControl(cKey, new UntypedFormControl('', optional ? [] : audioRecorderValidator()))
    })

    return form
  }

  set rows(rows) {
    this._rows = rows
  }

  set rowsOrder(rowsOrder) {
    this._rowsOrder = rowsOrder
  }

  get rowKeys() {
    return Object.keys(this._rows)
  }

  get rows() {
    return this._rows
  }

  get rowsOrder() {
    return this._rowsOrder
  }

  get randomizeRows() {
    return this._randomizeRows
  }

  get rowsRulesIndex() {
    return this._rowsRulesIndex
  }

  get useImage() {
    return this._useImage
  }

}
